import type {
  Ack,
  ApiError,
  ClientToServerEvents,
  CommandName,
  CommandPayload,
  CommandResults,
  DurableEvent,
  RoomSnapshot,
  ServerToClientEvents,
  SessionNotice,
} from '@crowdset/contracts';
import { SOCKET_PATH } from '@crowdset/contracts/constants';
import { io, type Socket } from 'socket.io-client';
import { clock, localNow } from './clock';
import { applyEvent, fromSnapshot } from './reducer';
import { EventSequencer } from './sequencer';
import { useRoomStore } from './store';

type CsSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

const SNAPSHOT_INTERVAL_MS = 15_000;
const CLOCK_REFRESH_MS = 60_000;
const COMMAND_TIMEOUT_MS = 8_000;
/** Join errors that must not be retried automatically. */
const TERMINAL_JOIN_ERRORS = new Set([
  'ROOM_FULL',
  'BANNED',
  'FORBIDDEN',
  'NOT_FOUND',
  'ROOM_UNAVAILABLE',
  'UNAUTHENTICATED',
  'VERIFIED_ACCOUNT_REQUIRED',
]);
/** Session notices that end this room session (no auto-rejoin). */
const TERMINAL_NOTICES = new Set<SessionNotice['kind']>([
  'moved_to_other_room',
  'kicked',
  'banned',
  'signed_out',
  'session_expired',
  'room_archived',
  'room_suspended',
  'access_revoked',
]);

export const newRequestId = (): string =>
  typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

/**
 * One live room session: socket lifecycle, clock sync, join/snapshot handling and ordered
 * event application. Exactly one instance exists while a room page is mounted.
 */
export class RoomConnection {
  private socket: CsSocket;
  private sequencer: EventSequencer;
  private timers: ReturnType<typeof setInterval>[] = [];
  private snapshotInFlight = false;
  private stopped = false;
  private clockBusy = false;

  constructor(private readonly slug: string) {
    const store = useRoomStore.getState();
    store.set({
      slug,
      status: 'connecting',
      error: null,
      notice: null,
      data: null,
      lastSyncAt: null,
    });

    this.sequencer = new EventSequencer({
      apply: (ev) => this.applyDurable(ev),
      requestSnapshot: () => void this.fetchSnapshot('gap'),
    });

    this.socket = io({
      path: SOCKET_PATH,
      transports: ['websocket'],
      withCredentials: true,
      autoConnect: false,
      reconnection: true,
      reconnectionDelay: 800,
      reconnectionDelayMax: 8_000,
      randomizationFactor: 0.5,
    }) as CsSocket;

    this.socket.on('connect', () => void this.onConnect());
    this.socket.on('disconnect', (reason) => {
      if (this.stopped) return;
      this.sequencer.reset();
      this.snapshotInFlight = false;
      const s = useRoomStore.getState();
      if (s.status === 'ended' || s.status === 'error') return;
      s.set({ status: reason === 'io client disconnect' ? 'offline' : 'reconnecting' });
    });
    this.socket.on('connect_error', () => {
      if (this.stopped) return;
      const s = useRoomStore.getState();
      if (s.status === 'ended' || s.status === 'error') return;
      s.set({ status: navigator.onLine === false ? 'offline' : 'reconnecting' });
    });
    this.socket.on('room.event', (ev) => {
      const d = useRoomStore.getState().data;
      if (d && ev.roomId !== d.room.id) return;
      this.sequencer.onEvent(ev);
    });
    this.socket.on('room.presence', (ev) =>
      useRoomStore.getState().setParticipants(ev.roomId, ev.participants),
    );
    this.socket.on('session.notice', (n) => this.onNotice(n));
    this.socket.on('server.status', (st) => {
      useRoomStore.getState().set({ serverStatus: st });
      // After a degraded period, reconcile from a fresh snapshot.
      if (st.realtime === 'ok' && st.database === 'ok') void this.fetchSnapshot('recovered');
    });

    this.timers.push(setInterval(() => void this.fetchSnapshot('periodic'), SNAPSHOT_INTERVAL_MS));
    this.timers.push(setInterval(() => void this.syncClock(3), CLOCK_REFRESH_MS));
    document.addEventListener('visibilitychange', this.onVisibility);
    window.addEventListener('online', this.onOnline);
    window.addEventListener('offline', this.onOffline);

    this.socket.connect();
  }

  stop(): void {
    this.stopped = true;
    for (const t of this.timers) clearInterval(t);
    document.removeEventListener('visibilitychange', this.onVisibility);
    window.removeEventListener('online', this.onOnline);
    window.removeEventListener('offline', this.onOffline);
    this.socket.removeAllListeners();
    this.socket.disconnect();
    useRoomStore.getState().set({ status: 'idle', slug: null });
  }

  /** Deliberately leave (removes you from the waitlist immediately), then stop. */
  async leave(): Promise<void> {
    try {
      await this.command('room.leave', { requestId: newRequestId() });
    } finally {
      this.stop();
    }
  }

  retry(): void {
    const s = useRoomStore.getState();
    s.set({ status: 'connecting', error: null, notice: null });
    if (this.socket.connected) void this.join();
    else this.socket.connect();
  }

  async command<N extends CommandName>(
    name: N,
    payload: CommandPayload<N>,
  ): Promise<Ack<CommandResults[N]>> {
    if (!this.socket.connected) {
      return { ok: false, error: { code: 'DEGRADED', message: 'Not connected. Reconnecting…' } };
    }
    try {
      const emitter = this.socket.timeout(COMMAND_TIMEOUT_MS) as unknown as {
        emitWithAck(ev: string, p: unknown): Promise<Ack<CommandResults[N]>>;
      };
      return await emitter.emitWithAck(name, payload);
    } catch {
      return {
        ok: false,
        error: { code: 'DEGRADED', message: 'The server did not respond in time.' },
      };
    }
  }

  private onVisibility = () => {
    if (document.visibilityState === 'visible' && this.socket.connected) {
      // After tab wake: re-estimate the clock and take a fresh snapshot before resuming.
      void this.syncClock(3).then(() => this.fetchSnapshot('visible'));
    }
  };

  /** The browser knows the network is gone: say so now instead of waiting for a ping timeout. */
  private onOffline = () => {
    const s = useRoomStore.getState();
    if (this.stopped || s.status === 'ended' || s.status === 'error') return;
    s.set({ status: 'offline' });
    // Drop the dead transport so Socket.IO starts reconnecting as soon as the network returns.
    this.socket.io.engine?.close();
  };

  private onOnline = () => {
    if (!this.socket.connected && !this.stopped) this.socket.connect();
  };

  private async onConnect() {
    const s = useRoomStore.getState();
    if (s.status === 'ended' || s.status === 'error') return;
    s.set({ status: 'joining' });
    clock.reset();
    await this.syncClock(5);
    await this.join();
  }

  private async syncClock(samples: number) {
    if (this.clockBusy || !this.socket.connected) return;
    this.clockBusy = true;
    try {
      for (let i = 0; i < samples; i++) {
        const t0 = localNow();
        const ack = await this.command('time.ping', { t0 });
        const t1 = localNow();
        if (ack.ok) clock.addSample(t0, t1, ack.data.serverNowMs);
      }
    } finally {
      this.clockBusy = false;
    }
  }

  private async join() {
    this.sequencer.reset();
    this.sequencer.beginSnapshot();
    this.snapshotInFlight = true;
    const ack = await this.command('room.join', { requestId: newRequestId(), roomSlug: this.slug });
    this.snapshotInFlight = false;
    if (this.stopped) return;
    if (!ack.ok) {
      this.handleJoinError(ack.error);
      return;
    }
    this.applySnapshot(ack.data);
    useRoomStore.getState().set({ status: 'live', error: null });
  }

  private handleJoinError(error: ApiError) {
    const s = useRoomStore.getState();
    this.sequencer.abortSnapshot();
    if (TERMINAL_JOIN_ERRORS.has(error.code)) {
      s.set({ status: 'error', error });
      this.socket.disconnect();
      return;
    }
    // Transient (degraded, rate limited): retry shortly while staying connected.
    s.set({ status: 'reconnecting', error });
    setTimeout(() => {
      if (!this.stopped && this.socket.connected && useRoomStore.getState().status !== 'live')
        void this.join();
    }, error.retryAfterMs ?? 2_000);
  }

  async fetchSnapshot(_reason: string): Promise<void> {
    const s = useRoomStore.getState();
    if (this.stopped || this.snapshotInFlight || !this.socket.connected || s.status !== 'live')
      return;
    this.snapshotInFlight = true;
    this.sequencer.beginSnapshot();
    const ack = await this.command('room.snapshot', { requestId: newRequestId() });
    this.snapshotInFlight = false;
    if (this.stopped) return;
    if (ack.ok) {
      this.applySnapshot(ack.data);
    } else if (ack.error.code === 'NOT_IN_ROOM') {
      // Membership lapsed (e.g. after a long sleep) — rejoin.
      await this.join();
    } else if (TERMINAL_JOIN_ERRORS.has(ack.error.code)) {
      this.handleJoinError(ack.error);
    } else {
      this.sequencer.abortSnapshot();
    }
  }

  private applySnapshot(snap: RoomSnapshot) {
    const s = useRoomStore.getState();
    clock.noteServerTime(snap.serverNowMs);
    const current = this.sequencer.currentVersion;
    // Never regress to an older snapshot than the events we already applied.
    if (current !== null && snap.roomVersion < current && s.data) {
      this.sequencer.onSnapshot(current);
      s.set({
        data: { ...s.data, participants: snap.participants, me: snap.me },
        lastSyncAt: Date.now(),
      });
      return;
    }
    s.set({ data: fromSnapshot(snap, s.data), lastSyncAt: Date.now() });
    this.sequencer.onSnapshot(snap.roomVersion);
  }

  private applyDurable(ev: DurableEvent) {
    const s = useRoomStore.getState();
    if (!s.data) return;
    const next = applyEvent(s.data, ev);
    s.set({ data: next, lastSyncAt: Date.now() });
    if (
      ev.type === 'moderation.action' &&
      ev.payload.targetUserId &&
      ev.payload.targetUserId === s.data.me.userId
    ) {
      // Our own permissions may have changed (mute etc.); reconcile.
      void this.fetchSnapshot('self-moderated');
    }
    if (ev.type === 'dj.notice' && ev.payload.userId === s.data.me.userId) {
      s.toast(ev.payload.message, 'warn');
    }
  }

  private onNotice(n: SessionNotice) {
    const s = useRoomStore.getState();
    const d = s.data;
    if (n.roomId && d && n.roomId !== d.room.id) return;
    if (TERMINAL_NOTICES.has(n.kind)) {
      s.set({ status: 'ended', notice: n });
      this.stopped = true;
      this.socket.disconnect();
    } else {
      s.toast(n.message, 'warn');
    }
  }
}
